/**
 * WebAuthn helpers, base64url conversion + high-level ceremony helpers
 * used by both the app Login/Profile pages and the admin console.
 *
 * `startAdminPasskeyEnroll`, enroll a new passkey against /api/admin/passkey/*
 * `startAdminPasskeyLogin` , step-up ceremony against /api/admin/passkey/*
 */
import axios from 'axios';
import { BACKEND_URL } from '../lib/api';

const API = BACKEND_URL;

// ── base64url ↔ ArrayBuffer ─────────────────────────────────────────
export function b64ToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
  const binary = atob(base64 + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function bufferToB64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export const isPasskeySupported = () =>
  typeof window !== 'undefined' && !!window.PublicKeyCredential;

// ── Admin passkey ENROLLMENT (requires full session token) ─────────
export async function enrollAdminPasskey(sessionToken, passkeyName = 'My Passkey') {
  if (!isPasskeySupported()) throw new Error('Passkeys are not supported in this browser.');

  const auth = { Authorization: `Bearer ${sessionToken}` };

  // 1. Options
  const { data: optData } = await axios.post(`${API}/api/admin/passkey/enroll-options`, {}, { headers: auth });
  const options = JSON.parse(optData.options);
  options.challenge = b64ToBuffer(options.challenge);
  options.user.id = b64ToBuffer(options.user.id);
  if (options.excludeCredentials) {
    options.excludeCredentials = options.excludeCredentials.map(c => ({ ...c, id: b64ToBuffer(c.id) }));
  }

  // 2. Browser ceremony (Touch ID / Windows Hello / YubiKey)
  const credential = await navigator.credentials.create({ publicKey: options });

  // 3. Verify
  const credentialJSON = {
    id: credential.id,
    rawId: bufferToB64(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferToB64(credential.response.attestationObject),
      clientDataJSON: bufferToB64(credential.response.clientDataJSON),
    },
  };
  const { data } = await axios.post(`${API}/api/admin/passkey/enroll-verify`,
    { credential: credentialJSON, name: passkeyName },
    { headers: auth });
  return data;
}

// ── Admin STEP-UP authentication (requires stepup token from /admin/auth) ─
export async function verifyAdminPasskey(stepupToken) {
  if (!isPasskeySupported()) throw new Error('Passkeys are not supported in this browser.');

  const auth = { Authorization: `Bearer ${stepupToken}` };

  // 1. Options
  const { data: optData } = await axios.post(`${API}/api/admin/passkey/step-up-options`, {}, { headers: auth });
  const options = JSON.parse(optData.options);
  options.challenge = b64ToBuffer(options.challenge);
  if (options.allowCredentials) {
    options.allowCredentials = options.allowCredentials.map(c => ({ ...c, id: b64ToBuffer(c.id) }));
  }

  // 2. Browser ceremony
  const assertion = await navigator.credentials.get({ publicKey: options });

  // 3. Verify, returns the real 8-hour admin session token
  const credentialJSON = {
    id: assertion.id,
    rawId: bufferToB64(assertion.rawId),
    type: assertion.type,
    response: {
      authenticatorData: bufferToB64(assertion.response.authenticatorData),
      clientDataJSON: bufferToB64(assertion.response.clientDataJSON),
      signature: bufferToB64(assertion.response.signature),
      userHandle: assertion.response.userHandle ? bufferToB64(assertion.response.userHandle) : null,
    },
  };
  const { data } = await axios.post(`${API}/api/admin/passkey/step-up-verify`,
    { credential: credentialJSON },
    { headers: auth });
  return data;   // { ok, token, expires_in, email }
}
